import {Button, Stack, Typography} from '@mui/material';
import {useObservable} from '@ngneat/react-rxjs';
import {useSnackbar} from 'notistack';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {finalize} from 'rxjs';

import {indexationService, ManageIndexation} from '@store/settings/indexation';
import {serviceKindsQuery, serviceKindsService} from '@store/settings/serviceKinds';

import {checkIfErrors, FieldValidationType, getFieldError} from '@utils/yup.utils';

import DatePickerComponent from '@components/inputs/datePicker/DatePicker.component';
import NumberTextFieldComponent from '@components/inputs/numberTextField/NumberTextField.component';
import SelectComponent from '@components/inputs/select/Select.component';
import TextFieldComponent from '@components/inputs/textField/TextField.component';
import ModalComponent from '@components/modal/Modal.component';

interface ManageIndexationModalProps {
  handleClose: () => void;
}

const ManageIndexationModal = (props: ManageIndexationModalProps) => {
  const {handleClose} = props;

  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [indexation, setIndexation] = useState<ManageIndexation>({});

  const [{serviceKinds}] = useObservable(serviceKindsQuery.serviceKinds$);

  useEffect(() => {
    serviceKindsService.getServiceKinds().subscribe({
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  }, [enqueueSnackbar]);

  const update = (name: string) => (value: any) => setIndexation((state) => ({...state, [name]: value}));

  const handleSave = () => {
    setLoading(true);
    indexationService.createIndexation(indexation).pipe(finalize(() => setLoading(false)))
      .subscribe({
        next: () => {
          enqueueSnackbar(t('settings.indexation.success.add'), {variant: 'success'});
          handleClose();
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
  };

  const errors = {
    name: getFieldError(indexation.name, FieldValidationType.REQUIRED_STRING),
    invoiceName: getFieldError(indexation.invoiceName, FieldValidationType.REQUIRED_STRING),
    code: getFieldError(indexation.code, FieldValidationType.REQUIRED_STRING),
    serviceKindId: getFieldError(indexation.serviceKindId, FieldValidationType.REQUIRED_NUMBER),
    initialRate: getFieldError(indexation.initialRate, FieldValidationType.REQUIRED_NUMBER),
    initialDate: getFieldError(indexation.initialDate, FieldValidationType.REQUIRED_STRING),
  };

  return (
    <ModalComponent
      title={t('settings.indexation.drawer.title')}
      handleClose={handleClose}
      content={(
        <Stack width="70%" spacing={2} pt="55px" pb="10px">
          <TextFieldComponent
            color="secondary"
            labelWidth={180}
            label={t('settings.indexation.drawer.fields.name')}
            placeholder={t('global.writeSomething')}
            handleChange={update('name')}
            value={indexation.name || ''}
            error={errors.name}
            required/>
          <TextFieldComponent
            color="secondary"
            labelWidth={180}
            label={t('settings.indexation.drawer.fields.invoiceName')}
            placeholder={t('global.writeSomething')}
            handleChange={update('invoiceName')}
            value={indexation.invoiceName || ''}
            error={errors.invoiceName}
            required/>
          <TextFieldComponent
            color="secondary"
            labelWidth={180}
            label={t('settings.indexation.drawer.fields.code')}
            placeholder={t('global.writeSomething')}
            handleChange={update('code')}
            value={indexation.code || ''}
            error={errors.code}
            required/>
          <SelectComponent
            color="secondary"
            labelWidth={180}
            label={t('settings.indexation.drawer.fields.serviceKind')}
            placeholder={t('global.selectOption')}
            handleChange={update('serviceKindId')}
            value={indexation.serviceKindId}
            error={errors.serviceKindId}
            items={serviceKinds.map((sk) => ({value: sk.id, label: sk.kind}))}
            required/>
          <Stack spacing={0.5}>
            <NumberTextFieldComponent
              color="secondary"
              labelWidth={180}
              label={t('settings.indexation.addRate.fields.rateInPercent')}
              placeholder={t('global.writeSomething')}
              pattern={new RegExp(/^(\d*|\d+)[,.]?\d*$/)}
              endAdornment={<Typography fontSize={16}>%</Typography>}
              handleChange={update('initialRate')}
              value={indexation.initialRate ?? undefined}
              error={errors.initialRate}
              required/>
            <Typography fontSize={12} color="text.secondary" pl="188px">
              {t('settings.indexation.addRate.fields.rateInPercentHelper')}
            </Typography>
          </Stack>
          <Stack spacing={0.5}>
            <DatePickerComponent
              color="primary"
              labelWidth={180}
              label={t('settings.indexation.addRate.fields.fromDate')}
              handleChange={update('initialDate')}
              value={indexation.initialDate}
              error={errors.initialDate}
              required/>
            <Typography fontSize={12} color="text.secondary" pl="188px">
              {t('settings.indexation.addRate.fields.fromDateHelper')}
            </Typography>
          </Stack>
        </Stack>
      )}
      actions={(
        <Button
          variant="contained"
          disabled={loading || checkIfErrors(errors)}
          onClick={handleSave}
          color="primary">
          {t('global.save')}
        </Button>
      )}/>
  );
};

export default ManageIndexationModal;
